import { memo, useMemo } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./EChart";
import { defaultAxisLabel, defaultGrid, tooltipCss } from "./chartDefaults";
import type { DimensionSeries } from "../../config/data";
import type { AxisFormatter } from "./XmrChart";
import ChartFrame from "../ChartInspector";
import type { MeasureDef } from "../../config/metrics";

interface DimensionLineChartProps {
  title: string;
  data: DimensionSeries;
  axisFormatter?: AxisFormatter;
  measure?: MeasureDef;
  contextLabel?: string;
  axisName?: string;
}

function DimensionLineChart({ title, data, axisFormatter, measure, contextLabel, axisName }: DimensionLineChartProps) {
  const option = useMemo<EChartsOption>(
    () => buildOption(data, axisFormatter, axisName),
    [data, axisFormatter, axisName],
  );

  return (
    <ChartFrame
      title={title}
      data={data}
      chartLabel="Dimension line chart"
      measure={measure}
      axisFormatter={axisFormatter}
      contextLabel={contextLabel}
    >
      <EChart option={option} height={360} />
    </ChartFrame>
  );
}

function buildOption(data: DimensionSeries, axisFormatter?: AxisFormatter, axisName?: string): EChartsOption {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return {
      xAxis: { type: "time" },
      yAxis: { type: "value" },
      series: [],
    } satisfies EChartsOption;
  }

  const timestampSet = new Set<number>();
  for (const [, series] of entries) {
    for (const date of series.timestamps) {
      timestampSet.add(date.getTime());
    }
  }
  const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

  const seriesOptions: SeriesOption[] = entries.map(([dimension, series]) => {
    const lookup = new Map<number, number>();
    for (let i = 0; i < series.timestamps.length; i++) {
      lookup.set(series.timestamps[i].getTime(), series.data[i]);
    }
    const values = timestamps.map(ms => lookup.get(ms) ?? 0);
    return {
      name: dimension,
      type: "line",
      data: timestamps.map((ms, idx) => [ms, values[idx]]),
      symbol: "circle",
      symbolSize: 6,
      smooth: true,
    } satisfies SeriesOption;
  });

  return {
    animation: false,
    tooltip: { trigger: "axis", axisPointer: { type: "cross" }, extraCssText: tooltipCss },
    legend: { top: 0 },
    grid: defaultGrid,
    xAxis: { type: "time", boundaryGap: false },
    yAxis: {
      type: "value",
      axisLabel: { formatter: axisFormatter ?? defaultAxisLabel, margin: 12 },
      name: axisName,
      nameLocation: "end",
      nameGap: 30,
    },
    series: seriesOptions,
  } satisfies EChartsOption;
}

export default memo(DimensionLineChart);
